import React, { FC, ReactNode, useState, useContext } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { Button, CircularProgress, Grid } from '@mui/material';

import AcceptanceConfirmationModal from '../../../sharedModules/layout/AcceptanceConfirmationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';

import { backflowLogRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowLog, BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowLogSubmitButtonsProps {
  backflowJobItem: BackflowJobItem;
  formValues: BackflowLog;
  outOfService: boolean;
  fetchBackflowJobItem: () => void;
}

const BackflowLogSubmitButtons: FC<BackflowLogSubmitButtonsProps> = (props) => {
  const { formAction } = useParams<{ formAction?: string }>();
  const location = useLocation();

  const approveMode = formAction === 'approve';

  const history = useHistory();

  const { backflowJobItem, formValues, outOfService, fetchBackflowJobItem } = props;

  // Get backLink from URL params
  const searchParams = new URLSearchParams(location.search);
  const backLinkParam = searchParams.get('backLink');

  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [acceptanceConfirmationModalOpen, setAcceptanceConfirmationModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>();

  // If using the full form, many fields are required.
  // If using the comment-only form, only the comment is required.
  const requiredFields = outOfService
    ? [{ key: 'internal_comment', label: 'Comment' }]
    : backflowLogRequiredFields(backflowJobItem.backflow_type);

  const submitStatus = () => {
    if (outOfService) return 'out_of_service';
    if (approveMode) return 'approved';
    return 'pending_approval';
  };

  const submitJobItem = async () => {
    if (loading) return; // guard double-taps, including modal confirm

    try {
      setLoading(true);

      await axios.put(`/backflows/backflow_logs/${formValues.id}`, { ...formValues, status: submitStatus() });

      // Refetch the job item to get the updated submitted_at value
      await fetchBackflowJobItem();

      setSnackBarMessage('Backflow test form submitted successfully!');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');

      // Redirect after a short delay
      let redirectUrl = '';

      // If there's a backLink parameter, use that
      if (backLinkParam) {
        redirectUrl = backLinkParam;
      } else {
        // Otherwise, default to job group page
        let urlSuffix = '';
        if (approveMode) urlSuffix = '/approve';
        redirectUrl = `/backflow_tests/backflow_job_groups/${backflowJobItem?.backflow_job_group_id}${urlSuffix}`;
      }

      setTimeout(() => {
        history.push(redirectUrl);
      }, 2000);
    } catch (error) {
      setErrorMessage('Failed to submit form. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmitClicked = async () => {
    const validate = validateRequiredFields(formValues, requiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    if (approveMode && backflowJobItem.is_final_approval_item) {
      setAcceptanceConfirmationModalOpen(true);
    } else {
      submitJobItem();
    }
  };

  const confirmMessage = (
    <p>
      This will approve the final Backflow Device for this work order. Doing so will notify all Tudi employees who
      receive approval notifications and submit the updated device details to Servman. This cannot be undone.
    </p>
  );

  const buttonLabel = () => {
    if (backflowJobItem?.status === 'approved') return 'Approved';
    if (approveMode) return 'Approve';
    return 'Submit for Approval';
  };

  const buttonDisabled = loading || backflowJobItem?.status === 'approved';

  return (
    <Grid container>
      <AcceptanceConfirmationModal
        onConfirm={submitJobItem}
        message={confirmMessage}
        modalOpen={acceptanceConfirmationModalOpen}
        setModalOpen={setAcceptanceConfirmationModalOpen}
        title="Approve Final Device for this Work Order"
      />
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
        <ErrorMessage message={validationErrors} />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmitClicked}
          disabled={buttonDisabled}
          sx={{ minWidth: 150 }}
        >
          {loading ? <CircularProgress size={24} /> : buttonLabel()}
        </Button>
      </Grid>
    </Grid>
  );
};

export default BackflowLogSubmitButtons;
